import { Fragment, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { dateHandler } from 'pages/globals/global';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularWithPath from 'components/@extended/progress/CircularWithPath';
import { useGetNotificationMutation, useMarkReadNotificationMutation } from 'store/features/process/processApi';
import EmptyUI from 'components/@extended/Epmty';
import { BellIcon } from 'assets/icons/common';

const LIST_SX = {
	width: '100%',
	maxWidth: '100%',
	height: 430,
	maxHeight: 430,
	backgroundColor: '#FFFFFF',
	overflow: 'auto',
	overscrollBehavior: 'contain',
	'&::-webkit-scrollbar': {
		width: '6px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#aeb0b0',
		borderRadius: '6px',
	},
	p: '10px 20px 12px 20px',
};

const SECTION_TITLE_SX = { color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 };
const getItemSx = (isUnread) => ({
	bgcolor: isUnread ? '#E8F3FF' : '#F9FAFB',
	borderRadius: '14px',
	px: 1.5,
	py: 1.25,
	mb: '12px',
	'&:hover': { bgcolor: isUnread ? '#DDEEFF' : '#F3F4F6' },
});

const getAvatarSx = (isUnread) => ({
	background: isUnread ? '#155DFC' : 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
	p: '8px',
	borderRadius: '50%',
	border: 'none',
	width: 40,
	height: 40,
	position: 'relative',
	overflow: 'visible',
	'&::after': isUnread
		? {
				content: '""',
				position: 'absolute',
				top: -1,
				right: 4,
				width: 10,
				height: 10,
				borderRadius: '50%',
				backgroundColor: '#155DFC',
				border: '2px solid #FFFFFF',
				zIndex: 2,
			}
		: {},
});

const renderNotificationPrimary = (value) => (
	<Typography sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.45 }}>
		{value?.notificationParts &&
			value?.notificationParts.length > 0 &&
			value?.notificationParts.map((item, index) =>
				item?.style === 'BOLD' ? (
					<Typography key={index} component="span" sx={{ color: '#1E293B', fontWeight: 500, fontSize: '14px' }}>
						{item?.phrase}
					</Typography>
				) : (
					item?.phrase
				)
			)}
	</Typography>
);

const isUnreadNotification = (value) => value?.notificationTo?.clicked !== 'clicked';

function NotificationContent({ searchString, clickHandler }) {
	const [GetNotification, { data: notificationData, isSuccess: isNotificationSuccess, isLoading }] = useGetNotificationMutation();
	const [MarkReadNotification] = useMarkReadNotificationMutation();

	const size = 10;
	const [pageNumber, setPageNumber] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [completedNotList, setCompletedNotList] = useState([]);
	const [newNotificationList, setNewNotificationList] = useState([]);

	const search = useMemo(() => {
		return [...newNotificationList, ...completedNotList]?.filter((item) => {
			const content = item?.notificationParts?.map((notificationPart) => notificationPart?.phrase).join('');

			return content.toLocaleLowerCase().includes(searchString.toLocaleLowerCase());
		});
		//eslint-disable-next-line
	}, [searchString]);

	const isDateOlder = (dateString) => {
		// Convert the given date string to a Date object
		const givenDate = new Date(dateString);

		// Get the current date and time
		const currentDate = new Date();

		// Subtract 15 days in milliseconds
		const sevenDaysAgo = new Date(currentDate.getTime() - 15 * 24 * 60 * 60 * 1000);

		// Compare the given date with 7 days ago
		return givenDate < sevenDaysAgo;
	};

	// useEffect(() => {
	//   dispatch(setMarkAsReadForNotification({ type }));
	// }, [type]);

	// useEffect(() => {
	//   if (newNotificationList.length > 0) {
	//     let unreadNotificationIdList = newNotificationList.map((item) => item.id);

	//     MarkReadNotification({
	//       notificationIdList: unreadNotificationIdList,
	//     });
	//   }
	// }, [newNotificationList]);

	useEffect(() => {
		if (pageNumber > -1 && size) {
			GetNotification({
				page: pageNumber,
				size,
			});
		}
	}, [pageNumber]);

	useEffect(() => {
		if (isNotificationSuccess) {
			const oldList = notificationData?.notifications?.length ?? 0;
			const newList = notificationData?.unreadNotifications?.length ?? 0;
			const total = notificationData?.totalNotification ?? 0;

			const currentCompletedNotList = completedNotList.length + oldList + newNotificationList.length + newList;

			if (currentCompletedNotList >= total) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}

			if (oldList > 0) {
				setCompletedNotList((prev) => [...prev, ...notificationData.notifications]);
			}

			if (newList > 0) {
				setNewNotificationList((prev) => [...prev, ...notificationData.unreadNotifications]);

				// let unreadNotificationIdList = notificationData.unreadNotifications.map((item) => item.id);

				MarkReadNotification({
					notificationIdList: [],
				});
			}
		}
	}, [isNotificationSuccess]);

	if (!hasMore && !isNotificationSuccess && isLoading) {
		return (
			<ListItem>
				<Stack width="100%" alignItems="center" justifyContent="center">
					<CircularWithPath size="medium" />
				</Stack>
			</ListItem>
		);
	}

	if (
		(isNotificationSuccess &&
			!isLoading &&
			notificationData?.notifications?.length === 0 &&
			notificationData?.unreadNotifications?.length === 0) ||
		(searchString !== '' && search?.length === 0)
	) {
		return <EmptyUI name={'Notification'} />;
	}

	if (!isLoading && searchString !== '') {
		return <NotificationSearchContent search={search} clickHandler={clickHandler} />;
	}

	return (
		<List
			id="completed-notification-list"
			component="nav"
			sx={LIST_SX}
		>
			<InfiniteScroll
				dataLength={completedNotList.length + newNotificationList.length}
				next={() => {
					setPageNumber((prev) => prev + 1);
				}}
				hasMore={hasMore}
				loader={
					<ListItem>
						<Stack width="100%" alignItems="center" justifyContent="center">
							<CircularWithPath size="medium" />
						</Stack>
					</ListItem>
				}
				scrollableTarget="completed-notification-list"
			>
				{/* New Section */}
				{newNotificationList?.length > 0 && searchString === '' && (
					<Box>
						<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
							<Typography sx={SECTION_TITLE_SX}>New</Typography>
						</Box>
						{/* <Divider /> */}
						{[...newNotificationList]?.map((value, index) => (
							<ListItemButton
								key={index}
								sx={getItemSx(isUnreadNotification(value))}
								onClick={() => clickHandler(value)}
							>
								<ListItemAvatar>
									<Avatar alt="notification" sx={getAvatarSx(isUnreadNotification(value))}>
										<BellIcon color={isUnreadNotification(value) ? '#FFFFFF' : '#155DFC'} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={renderNotificationPrimary(value)}
									secondary={<Typography sx={{ color: '#64748B', fontSize: '12px' }}>{dateHandler(value?.createdDate)}</Typography>}
								/>
							</ListItemButton>
						))}
					</Box>
				)}

				{/* Recent Section */}
				{completedNotList?.length > 0 && searchString === '' && (
					<Box>
						{completedNotList
							?.filter((item) => !isDateOlder(item?.createdDate?.localDateTimeStringAMPM))
							?.map((value, index) => (
								<Fragment key={index}>
									{index === 0 && (
										<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
											<Typography sx={SECTION_TITLE_SX}>Recent</Typography>
										</Box>
									)}
									<ListItemButton
										sx={getItemSx(isUnreadNotification(value))}
										onClick={() => clickHandler(value)}
									>
										<ListItemAvatar>
											<Avatar alt="notification" sx={getAvatarSx(isUnreadNotification(value))}>
												<BellIcon color={isUnreadNotification(value) ? '#FFFFFF' : '#155DFC'} />
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={renderNotificationPrimary(value)}
											secondary={<Typography sx={{ color: '#64748B', fontSize: '12px' }}>{dateHandler(value?.createdDate)}</Typography>}
										/>
									</ListItemButton>
								</Fragment>
							))}

						{/* Old Message Section */}
						{completedNotList
							?.filter((item) => isDateOlder(item?.createdDate?.localDateTimeStringAMPM))
							?.map((value, index) => (
								<Box key={index}>
									{index === 0 && (
										<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
											<Typography sx={SECTION_TITLE_SX}>Older</Typography>
										</Box>
									)}
									<ListItemButton
										sx={getItemSx(isUnreadNotification(value))}
										onClick={() => clickHandler(value)}
									>
										<ListItemAvatar>
											<Avatar alt="notification" sx={getAvatarSx(isUnreadNotification(value))}>
												<BellIcon color={isUnreadNotification(value) ? '#FFFFFF' : '#155DFC'} />
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={renderNotificationPrimary(value)}
											secondary={<Typography sx={{ color: '#64748B', fontSize: '12px' }}>{dateHandler(value?.createdDate)}</Typography>}
										/>
									</ListItemButton>
								</Box>
							))}
					</Box>
				)}
			</InfiniteScroll>
		</List>
	);
}

export default NotificationContent;

function NotificationSearchContent({ search, clickHandler }) {
	return (
		<List
			component="nav"
			sx={LIST_SX}
		>
			<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
				<Typography sx={SECTION_TITLE_SX}>Search</Typography>
			</Box>
			{search.map((value, index) => (
				<ListItemButton
					key={index}
					sx={getItemSx(isUnreadNotification(value))}
					onClick={() => clickHandler(value)}
				>
					<ListItemAvatar>
						<Avatar alt="notification" sx={getAvatarSx(isUnreadNotification(value))}>
							<BellIcon color={isUnreadNotification(value) ? '#FFFFFF' : '#155DFC'} />
						</Avatar>
					</ListItemAvatar>
					<ListItemText
						primary={renderNotificationPrimary(value)}
						secondary={<Typography sx={{ color: '#64748B', fontSize: '12px' }}>{dateHandler(value?.createdDate)}</Typography>}
					/>
				</ListItemButton>
			))}
		</List>
	);
}
